import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../store/UserContext';
import { useTheme } from '../hooks/useTheme';
import { t } from '../i18n';
import { APP_LOGO, APP_LOGO_ALT } from '../config/brand';
import Breadcrumb from './Breadcrumb';
import { notificationsApi } from '../api/notifications';
import {
    Bars3Icon,
    UserIcon,
    ChevronDownIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    BellIcon,
    SunIcon,
    MoonIcon,
    ViewColumnsIcon,
    Bars4Icon,
    ChevronDoubleUpIcon,
    ChevronDoubleDownIcon
} from '@heroicons/react/24/outline';
import HorizontalNav from './HorizontalNav';
import { processImageUrl } from '../utils/imageUtils';
import { navigateForTenantNotification } from '../utils/notificationNavigation';

const HEADER_DISPLAY_MODE_KEY = 'appHeaderDisplayMode';

function Header({ onLogout, onToggleMobileSidebar, showMobileSidebarToggle = true, mobileOpen, setMobileOpen }) {
    const { user } = useUser();
    const navigate = useNavigate();
    const { theme, navLayout, toggleTheme, toggleNavLayout } = useTheme();
    const isHorizontal = navLayout === 'horizontal';
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [avatarFailed, setAvatarFailed] = useState(false);
    const avatarSrc = processImageUrl(user?.avatarUrl || user?.avatar || '');
    const showAvatar = Boolean(avatarSrc) && !avatarFailed;
    const userMenuRef = useRef(null);
    const leaveTimerRef = useRef(null);
    const [hoverMode, setHoverMode] = useState(() => {
        try {
            return localStorage.getItem(HEADER_DISPLAY_MODE_KEY) === 'hover';
        } catch {
            return false;
        }
    });
    const [headerHovered, setHeaderHovered] = useState(false);
    const [navDropdownOpen, setNavDropdownOpen] = useState(false);

    const headerExpanded = !hoverMode || headerHovered || showUserMenu || showNotifications || navDropdownOpen;

    useEffect(() => {
        setAvatarFailed(false);
    }, [avatarSrc]);

    const clearLeaveTimer = () => {
        if (leaveTimerRef.current) {
            clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
    };

    const handleHeaderMouseEnter = () => {
        clearLeaveTimer();
        setHeaderHovered(true);
    };

    const handleHeaderMouseLeave = () => {
        clearLeaveTimer();
        leaveTimerRef.current = setTimeout(() => {
            setHeaderHovered(false);
            leaveTimerRef.current = null;
        }, 180);
    };

    const toggleHeaderDisplayMode = () => {
        setHoverMode((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(HEADER_DISPLAY_MODE_KEY, next ? 'hover' : 'fixed');
            } catch {
                /* ignore */
            }
            if (!next) {
                clearLeaveTimer();
                setHeaderHovered(false);
            }
            return next;
        });
    };

    // 格式化时间显示
    const formatTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const raw = typeof dateTimeString === 'string' ? dateTimeString.trim() : dateTimeString;
        // 后端 yyyy-MM-dd HH:mm:ss 与 ISO 字符串兼容（避免 Safari 解析失败）
        const normalized = typeof raw === 'string' && raw.includes(' ') && !raw.includes('T')
            ? raw.replace(' ', 'T')
            : raw;
        const date = new Date(normalized);
        if (Number.isNaN(date.getTime())) return '';
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('justNow') || '刚刚';
        if (diffMins < 60) return diffMins + (t('minutesAgo') || '分钟前');
        if (diffHours < 24) return diffHours + (t('hoursAgo') || '小时前');
        if (diffDays < 7) return diffDays + (t('daysAgo') || '天前');
        
        return date.toLocaleDateString();
    };

    // 加载通知数据
    const loadNotifications = async () => {
        if (!user?.id) return;
        
        try {
            setLoading(true);
            const [notificationsRes, countRes] = await Promise.all([
                notificationsApi.getUserNotifications(user.id),
                notificationsApi.getUnreadCount(user.id)
            ]);
            
            // 转换通知数据格式，兼容各种响应格式
            const notificationsData = Array.isArray(notificationsRes.data) 
                ? notificationsRes.data 
                : (notificationsRes.data?.data || []);
            
            const formattedNotifications = notificationsData.map(n => ({
                id: n.id,
                title: n.title,
                message: n.content,
                time: formatTime(n.createdAt),
                type: n.type,
                isRead: n.status === '已读'
            }));
            
            setNotifications(formattedNotifications);
            
            // 兼容不同的count响应格式
            const unreadCountValue = typeof countRes.data === 'number' 
                ? countRes.data 
                : (countRes.data?.count || 0);
            setUnreadCount(unreadCountValue);
        } catch (error) {
            console.error('加载通知失败:', error);
            // 如果加载失败，使用空数据而不是模拟数据
            setNotifications([]);
            setUnreadCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification?.id || !user?.id) return;
        if (!notification.isRead) {
            await handleMarkAsRead(notification.id);
        }
        navigateForTenantNotification(notification.type, navigate);
    };

    // 标记通知为已读
    const handleMarkAsRead = async (notificationId) => {
        if (!user?.id) return;
        
        try {
            await notificationsApi.markAsRead(notificationId, user.id);
            // 重新加载通知
            loadNotifications();
        } catch (error) {
            console.error('标记已读失败:', error);
        }
    };

    // 初始加载通知
    useEffect(() => {
        loadNotifications();
        
        // 每分钟刷新一次通知
        const interval = setInterval(loadNotifications, 60000);
        
        return () => clearInterval(interval);
    }, [user?.id]);

    // 点击外部关闭用户菜单
    useEffect(() => {
        function handleClickOutside(event) {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
                setShowNotifications(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => () => clearLeaveTimer(), []);

    const handleLogout = () => {
        onLogout();
        setShowUserMenu(false);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'warning':
                return <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>;
            case 'success':
                return <div className="w-2 h-2 bg-green-500 rounded-full"></div>;
            case 'error':
                return <div className="w-2 h-2 bg-red-500 rounded-full"></div>;
            default:
                return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
        }
    };

    return (
        <div
            className={`relative z-50 flex-shrink-0 ${hoverMode ? 'h-2.5' : ''}`}
            onMouseEnter={hoverMode ? handleHeaderMouseEnter : undefined}
            onMouseLeave={hoverMode ? handleHeaderMouseLeave : undefined}
        >
        <header
            className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 min-w-0 ${
                hoverMode
                    ? `absolute inset-x-0 top-0 transition-[max-height,box-shadow] duration-200 ${
                        headerExpanded
                            ? 'z-50 max-h-[120px] overflow-visible shadow-lg'
                            : 'z-50 max-h-2.5 overflow-hidden shadow-none border-b-0'
                    }`
                    : 'relative z-50 shadow-sm'
            }`}
        >
            {/* 窄屏：第 1 行 Logo + 右侧工具；第 2 行面包屑横向滚动。宽屏：单行三列 */}
            <div
                className={`grid gap-x-3 px-2 sm:px-4 min-w-0
                ${isHorizontal ? 'py-2 gap-y-0 min-[520px]:items-center min-[520px]:min-h-[52px]' : 'py-1.5 gap-y-1 items-center'}
                grid-cols-[auto_auto] min-[520px]:grid-cols-[auto_minmax(0,1fr)_auto]`}
            >
                <div className="col-start-1 row-start-1 flex items-center gap-1">
                    {showMobileSidebarToggle && !isHorizontal ? (
                        <button
                            type="button"
                            onClick={onToggleMobileSidebar}
                            className="h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 min-[520px]:hidden flex items-center justify-center shrink-0"
                            title="打开菜单"
                            aria-label="打开侧边栏菜单"
                        >
                            <Bars3Icon className="w-4 h-4" />
                        </button>
                    ) : null}
                <Link
                    to="/welcome"
                    className="col-start-1 row-start-1 flex items-center gap-1.5 sm:gap-2 group shrink-0 min-w-0 justify-self-start"
                >
                    <img
                        src={APP_LOGO}
                        alt={APP_LOGO_ALT}
                        className="h-7 sm:h-8 w-auto object-contain group-hover:scale-105 transition-transform duration-200 shrink-0"
                    />
                    <span className="text-sm sm:text-lg font-bold bg-gradient-to-r from-green-600 to-blue-600 dark:from-green-400 dark:to-blue-400 bg-clip-text text-transparent whitespace-nowrap">
                        {t('systemName')}
                    </span>
                </Link>
                </div>

                <div className="col-start-2 row-start-1 flex items-center justify-end gap-1 sm:gap-2 shrink-0 min-[520px]:col-start-3 min-[520px]:row-start-1">
                    {/* 顶栏显示模式：仅 SVG，悬停提示「悬停展开 / 收起」 */}
                    <button
                        type="button"
                        onClick={toggleHeaderDisplayMode}
                        title={hoverMode ? '悬停展开' : '收起'}
                        aria-label={hoverMode ? '悬停展开' : '收起'}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-100 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                    >
                        {hoverMode ? (
                            <ChevronDoubleDownIcon className="h-4 w-4" />
                        ) : (
                            <ChevronDoubleUpIcon className="h-4 w-4" />
                        )}
                    </button>

                    {/* 用户菜单 */}
                    <div className="relative shrink-0" ref={userMenuRef}>
                        <button
                            type="button"
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg px-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 sm:gap-2 sm:px-2 max-[380px]:pr-1"
                            aria-expanded={showUserMenu}
                            aria-haspopup="menu"
                        >
                            <div className="relative shrink-0">
                                <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm ring-1 ring-black/5 dark:from-blue-600 dark:to-blue-700 dark:ring-white/10">
                                    {showAvatar ? (
                                        <img
                                            src={avatarSrc}
                                            alt={user?.realName || user?.username || t('user')}
                                            className="h-full w-full object-cover"
                                            onError={() => setAvatarFailed(true)}
                                        />
                                    ) : (
                                        <UserIcon className="h-4 w-4 text-white" strokeWidth={2} />
                                    )}
                                </div>
                                {unreadCount > 0 && (
                                    <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-red-500 px-0.5 text-[9px] font-bold leading-none text-white dark:border-gray-800">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </div>
                            <span className="text-xs font-medium leading-none text-gray-700 dark:text-gray-300 max-[420px]:hidden whitespace-nowrap">
                                {user?.realName || user?.username || t('user')}
                            </span>
                            <ChevronDownIcon
                                className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-200 max-[420px]:hidden dark:text-gray-500 ${
                                    showUserMenu ? 'rotate-180' : ''
                                }`}
                            />
                        </button>

                    {/* 下拉菜单 */}
                    {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                            <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm dark:from-blue-600 dark:to-blue-700">
                                    {showAvatar ? (
                                        <img
                                            src={avatarSrc}
                                            alt=""
                                            className="h-full w-full object-cover"
                                            onError={() => setAvatarFailed(true)}
                                        />
                                    ) : (
                                        <UserIcon className="h-5 w-5 text-white" strokeWidth={2} />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                        {user?.realName || user?.username || t('user')}
                                    </p>
                                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                        {user?.email || 'user@example.com'}
                                    </p>
                                </div>
                            </div>

                            {/* 通知中心 */}
                            <div className="border-b border-gray-200 dark:border-gray-700">
                                <div className="px-4 py-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('notifications')}</span>
                                        <button
                                            onClick={() => setShowNotifications(!showNotifications)}
                                            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 relative"
                                        >
                                            <BellIcon className="w-4 h-4" />
                                            <span>{notifications.length}</span>
                                            {/* 未读通知提示 */}
                                            {unreadCount > 0 && (
                                                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-red-500 px-0.5 text-[9px] font-bold leading-none text-white dark:border-gray-800">
                                                    {unreadCount > 9 ? '9+' : unreadCount}
                                                </span>
                                            )}
                                        </button>
                                    </div>

                                    {showNotifications && (
                                        <div className="mt-2 max-h-48 overflow-y-auto">
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <p className="text-xs text-gray-500">{t('loading') || '加载中...'}</p>
                                                </div>
                                            ) : notifications.length === 0 ? (
                                                <div className="text-center py-4">
                                                    <p className="text-xs text-gray-500">{t('noNotifications') || '暂无通知'}</p>
                                                </div>
                                            ) : (
                                                notifications.map((notification) => (
                                                    <div 
                                                        key={notification.id} 
                                                        className={`flex items-start space-x-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                                                        onClick={() => handleNotificationClick(notification)}
                                                    >
                                                        {getNotificationIcon(notification.type)}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-2">
                                                                <p className={`text-xs font-medium ${!notification.isRead ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`}>
                                                                    {notification.title}
                                                                </p>
                                                                {!notification.isRead && (
                                                                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                {notification.message}
                                                            </p>
                                                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                                                {notification.time}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 主题切换 */}
                            <button
                                onClick={toggleTheme}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                {theme === 'light' ? (
                                    <MoonIcon className="w-4 h-4 mr-2" />
                                ) : (
                                    <SunIcon className="w-4 h-4 mr-2" />
                                )}
                                {theme === 'light' ? t('darkMode') : t('lightMode')}
                            </button>

                            {/* 导航布局切换 */}
                            <button
                                type="button"
                                onClick={toggleNavLayout}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                {isHorizontal ? (
                                    <ViewColumnsIcon className="w-4 h-4 mr-2" />
                                ) : (
                                    <Bars4Icon className="w-4 h-4 mr-2" />
                                )}
                                {isHorizontal ? '切换为左侧菜单' : '切换为顶部菜单'}
                            </button>

                            <Link
                                to="/profile"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setShowUserMenu(false)}
                            >
                                <UserIcon className="w-4 h-4 mr-2" />
                                {t('profile')}
                            </Link>

                            <Link
                                to="/profile/account-settings"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => setShowUserMenu(false)}
                            >
                                <Cog6ToothIcon className="w-4 h-4 mr-2" />
                                {t('accountSettings')}
                            </Link>

                            <div className="border-t border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                                    {t('logout')}
                                </button>
                            </div>
                        </div>
                    )}
                    </div>
                </div>

                <div
                    className={`col-span-2 row-start-2 min-w-0 empty:hidden empty:p-0 empty:m-0 min-[520px]:row-start-1 min-[520px]:col-start-2 min-[520px]:col-span-1 ${
                      isHorizontal
                        ? 'overflow-visible min-[520px]:flex min-[520px]:items-center min-[520px]:self-stretch min-[520px]:px-1'
                        : 'overflow-x-auto overflow-y-hidden pb-0.5 pl-1 pr-0.5 [scrollbar-width:thin]'
                    }`}
                    style={isHorizontal ? undefined : { WebkitOverflowScrolling: 'touch' }}
                >
                    {isHorizontal ? (
                        <HorizontalNav
                            mobileOpen={mobileOpen}
                            setMobileOpen={setMobileOpen}
                            onDropdownOpenChange={setNavDropdownOpen}
                        />
                    ) : (
                        <Breadcrumb />
                    )}
                </div>
            </div>
        </header>
        {hoverMode && !headerExpanded ? (
            <div
                className="pointer-events-none absolute inset-x-0 top-1 z-[51] flex justify-center"
                aria-hidden="true"
            >
                <span className="h-0.5 w-9 rounded-full bg-gray-300 dark:bg-gray-500" />
            </div>
        ) : null}
        </div>
    );
}

export default Header; 